import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Wallet,
  CreditCard,
  Building2,
  User,
  Phone,
  FilePenLine,
  Calendar,
  Trash2,
  AlertCircle,
  Pencil,
  Download,
  FileText,
  Hash,
} from "lucide-react";
import {
  Button,
  Card,
  Divider,
  Typography,
  Modal,
  Box,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { successToast, errorToast } from "../../../../services/toast.service";

// Mock data for testing
const MOCK_RECEIPTS = [
  {
    id: "rcpt001",
    firmId: "firm001",
    firmName: "Madhur Creation",
    date: "15/07/2023",
    amount: 15000,
    paymentMode: "Cash",
    adjustedBills: ["bill001", "bill002"],
    notes: "Payment for May invoices",
  },
];

const MOCK_FIRMS = [
  {
    id: "firm001",
    firmName: "Madhur Creation",
    ownerName: "Chaitanya Goyal",
    phoneNumber: "+91 7898747967",
    gstin: "78115264569",
    address: "31, Dwarka dham colony mangliya",
    outstandingAmount: 78500,
  },
];

const MOCK_BILLS = {
  firm001: [
    {
      id: "bill001",
      billNumber: "INV/2023/001",
      billDate: "12/05/2023",
      totalAmount: 35000,
      pendingAmount: 18000,
    },
    {
      id: "bill002",
      billNumber: "INV/2023/005",
      billDate: "22/05/2023",
      totalAmount: 42000,
      pendingAmount: 25000,
    },
  ],
};

const ViewReceiptDetail = () => {
  const { receiptId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [receiptData, setReceiptData] = useState(null);
  const [firmData, setFirmData] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    // Simulate API call to fetch receipt details
    setLoading(true);

    setTimeout(() => {
      // Try both with imported and mock data
      const receipt =
        MOCK_RECEIPTS.find(r => r.id === receiptId) || MOCK_RECEIPTS[0]; // Fall back to first mock receipt

      if (receipt) {
        setReceiptData(receipt);

        // Get firm details
        const firm = MOCK_FIRMS.find(firm => firm.id === receipt.firmId);
        setFirmData(firm);
      }

      setLoading(false);
    }, 800);
  }, [receiptId]);

  const handleEdit = () => {
    navigate(`/receipt/edit/${receiptId}`);
  };

  const handleDelete = () => {
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    // Simulate API call to delete receipt
    setLoading(true);

    setTimeout(() => {
      successToast(`Receipt ${receiptId} deleted successfully`);
      setDeleteModalOpen(false);
      // Navigate back to receipts list
      navigate("/receipt");
    }, 1000);
  };

  const handleBack = () => {
    navigate("/receipt");
  };

  // Format currency
  const formatCurrency = amount => {
    return `₹${amount.toLocaleString()}`;
  };

  // Get bill details from bill IDs
  const getBillDetails = billIds => {
    if (!billIds || !billIds.length) return [];

    const bills = [];
    billIds.forEach(billId => {
      // Search through all firms' bills
      Object.values(MOCK_BILLS).forEach(firmBills => {
        const bill = firmBills.find(b => b.id === billId);
        if (bill) bills.push(bill);
      });
    });

    return bills;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full bg-white">
        <CircularProgress size={40} sx={{ color: "#5652B0" }} />
      </div>
    );
  }

  if (!receiptData) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-white p-8">
        <AlertCircle size={48} className="text-red-500 mb-4" />
        <Typography variant="h6" className="font-outfit mb-2">
          Receipt Not Found
        </Typography>
        <Typography variant="body1" className="font-outfit text-gray-600 mb-6">
          The receipt {receiptId} doesn't exist or has been deleted.
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowLeft size={18} />}
          onClick={handleBack}
          sx={{
            backgroundColor: "#5652B0",
            "&:hover": { backgroundColor: "#4A47A3" },
            textTransform: "none",
            fontFamily: "Outfit, sans-serif",
          }}>
          Back to Receipts
        </Button>
      </div>
    );
  }

  const adjustedBills = getBillDetails(receiptData.adjustedBills);

  return (
    <div className="bg-white h-full overflow-auto">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="p-1.5 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Receipt Details
          </h1>
          <div
            className={`ml-4 px-3 py-1 rounded-full text-sm font-medium
            ${
              receiptData.paymentMode === "Cash"
                ? "bg-purple-100 text-purple-800"
                : "bg-green-100 text-green-800"
            }
          `}>
            {receiptData.paymentMode}
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outlined"
            startIcon={<Download size={16} />}
            sx={{
              textTransform: "none",
              borderColor: "#5652B0",
              color: "#5652B0",
              "&:hover": {
                borderColor: "#4A47A3",
                backgroundColor: "rgba(86, 82, 176, 0.04)",
              },
              fontFamily: "Outfit, sans-serif",
              borderRadius: "0.5rem",
            }}>
            Download
          </Button>
          <Button
            variant="outlined"
            startIcon={<Pencil size={16} />}
            onClick={handleEdit}
            sx={{
              textTransform: "none",
              borderColor: "#5652B0",
              color: "#5652B0",
              "&:hover": {
                borderColor: "#4A47A3",
                backgroundColor: "rgba(86, 82, 176, 0.04)",
              },
              fontFamily: "Outfit, sans-serif",
              borderRadius: "0.5rem",
            }}>
            Edit
          </Button>
          <Button
            variant="outlined"
            startIcon={<Trash2 size={16} />}
            onClick={handleDelete}
            sx={{
              textTransform: "none",
              borderColor: "#d32f2f",
              color: "#d32f2f",
              "&:hover": {
                borderColor: "#b71c1c",
                backgroundColor: "rgba(211, 47, 47, 0.04)",
              },
              fontFamily: "Outfit, sans-serif",
              borderRadius: "0.5rem",
            }}>
            Delete
          </Button>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 gap-6">
          {/* Receipt & Firm Information */}
          <Card
            sx={{
              boxShadow: "none",
              border: "1px solid rgba(229, 231, 235, 1)",
              borderRadius: "0.5rem",
            }}>
            <div className="flex items-center justify-between px-6 py-4 border-b bg-[#F9FAFB]">
              <div className="flex items-center">
                <div className="w-6 h-6 rounded-full bg-[rgba(86,82,176,0.1)] flex items-center justify-center mr-2">
                  <FileText size={14} className="text-[#5652B0]" />
                </div>
                <h2 className="text-base font-medium text-gray-800">
                  Receipt & Firm Information
                </h2>
              </div>
            </div>

            <div className="px-6 py-5">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Receipt Information */}
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase mb-3">
                    Receipt Details
                  </h3>

                  <table className="w-full text-sm">
                    <tbody>
                      <tr>
                        <td className="py-1.5 text-gray-500 pr-4 align-top w-1/3">
                          Receipt ID:
                        </td>
                        <td className="py-1.5 font-medium">{receiptData.id}</td>
                      </tr>
                      <tr>
                        <td className="py-1.5 text-gray-500 pr-4 align-top">
                          Date:
                        </td>
                        <td className="py-1.5 font-medium">
                          {receiptData.date}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-1.5 text-gray-500 pr-4 align-top">
                          Amount:
                        </td>
                        <td className="py-1.5 font-medium text-[#5652B0]">
                          {formatCurrency(receiptData.amount)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-1.5 text-gray-500 pr-4 align-top">
                          Payment Mode:
                        </td>
                        <td className="py-1.5">
                          <Chip
                            label={receiptData.paymentMode}
                            size="small"
                            icon={
                              receiptData.paymentMode === "Cash" ? (
                                <Wallet size={14} />
                              ) : (
                                <CreditCard size={14} />
                              )
                            }
                            sx={{
                              backgroundColor:
                                receiptData.paymentMode === "Cash"
                                  ? "rgba(124, 58, 237, 0.1)"
                                  : "rgba(16, 185, 129, 0.1)",
                              color:
                                receiptData.paymentMode === "Cash"
                                  ? "#7C3AED"
                                  : "#10B981",
                              fontWeight: 500,
                              fontSize: "0.7rem",
                              height: "20px",
                              borderRadius: "4px",
                              "& .MuiChip-icon": {
                                color: "inherit",
                                marginLeft: "4px",
                                marginRight: "-2px",
                              },
                              "& .MuiChip-label": { px: 1.5 },
                            }}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="py-1.5 text-gray-500 pr-4 align-top">
                          Notes:
                        </td>
                        <td className="py-1.5 font-medium">
                          {receiptData.notes || "-"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Firm Information */}
                <div className="border-t md:border-t-0 md:border-l border-gray-100 pt-4 md:pt-0 md:pl-6 mt-4 md:mt-0">
                  <h3 className="text-xs font-medium text-gray-500 uppercase mb-3">
                    Firm Details
                  </h3>

                  <table className="w-full text-sm">
                    <tbody>
                      <tr>
                        <td className="py-1.5 text-gray-500 pr-4 align-top w-1/3">
                          Firm:
                        </td>
                        <td className="py-1.5 font-medium">
                          {firmData.firmName}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-1.5 text-gray-500 pr-4 align-top">
                          Owner:
                        </td>
                        <td className="py-1.5 font-medium">
                          {firmData.ownerName}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-1.5 text-gray-500 pr-4 align-top">
                          GSTIN:
                        </td>
                        <td className="py-1.5 font-medium">{firmData.gstin}</td>
                      </tr>
                      <tr>
                        <td className="py-1.5 text-gray-500 pr-4 align-top">
                          Phone:
                        </td>
                        <td className="py-1.5 font-medium">
                          {firmData.phoneNumber}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-1.5 text-gray-500 pr-4 align-top">
                          Outstanding:
                        </td>
                        <td className="py-1.5 font-medium text-amber-600">
                          {formatCurrency(firmData.outstandingAmount)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Card>

          {/* Adjusted Bills */}
          <Card
            sx={{
              boxShadow: "none",
              border: "1px solid rgba(229, 231, 235, 1)",
              borderRadius: "0.5rem",
            }}>
            <div className="flex items-center justify-between px-6 py-4 border-b bg-[#F9FAFB]">
              <div className="flex items-center">
                <div className="w-6 h-6 rounded-full bg-[rgba(22,163,74,0.1)] flex items-center justify-center mr-2">
                  <Calendar size={14} className="text-[#16A34A]" />
                </div>
                <h2 className="text-base font-medium text-gray-800">
                  Adjusted Bills
                </h2>
              </div>
              <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                {adjustedBills.length}{" "}
                {adjustedBills.length === 1 ? "bill" : "bills"}
              </span>
            </div>

            <div className="px-6 py-5">
              {adjustedBills.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell className="text-[#6B7280] font-medium text-xs">
                          Bill Number
                        </TableCell>
                        <TableCell className="text-[#6B7280] font-medium text-xs">
                          Bill Date
                        </TableCell>
                        <TableCell className="text-[#6B7280] font-medium text-xs">
                          Total Amount
                        </TableCell>
                        <TableCell className="text-[#6B7280] font-medium text-xs">
                          Pending Amount
                        </TableCell>
                        <TableCell className="text-[#6B7280] font-medium text-xs">
                          Adjusted Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {adjustedBills.map(bill => (
                        <TableRow
                          key={bill.id}
                          hover
                          sx={{
                            "& td": {
                              paddingTop: "10px",
                              paddingBottom: "10px",
                              borderColor: "rgba(229, 231, 235, 0.5)",
                            },
                            "&:hover": {
                              backgroundColor: "rgba(249, 250, 251, 0.7)",
                            },
                          }}>
                          <TableCell className="font-medium text-xs text-[#5652B0]">
                            <div className="flex items-center">
                              <Hash size={12} className="text-gray-400 mr-1" />
                              {bill.billNumber}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs">
                            {bill.billDate}
                          </TableCell>
                          <TableCell className="text-xs">
                            {formatCurrency(bill.totalAmount)}
                          </TableCell>
                          <TableCell className="text-xs">
                            {formatCurrency(bill.pendingAmount)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={formatCurrency(
                                Math.min(
                                  receiptData.amount,
                                  bill.pendingAmount,
                                ),
                              )}
                              sx={{
                                height: 22,
                                fontSize: "0.65rem",
                                fontWeight: 500,
                                backgroundColor: "rgba(22, 163, 74, 0.1)",
                                color: "#16A34A",
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                  <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-4 border border-gray-200">
                    <FileText size={24} className="text-gray-400" />
                  </div>
                  <Typography
                    variant="body1"
                    className="text-gray-700 font-medium mb-1">
                    No Bills Adjusted
                  </Typography>
                  <Typography
                    variant="body2"
                    className="text-gray-500 max-w-md">
                    This receipt wasn't used to adjust any outstanding bills.
                  </Typography>
                  {receiptData.notes && (
                    <div className="mt-4 p-3 bg-gray-50 rounded border border-gray-200 text-left max-w-md w-full">
                      <div className="flex items-center mb-2">
                        <FilePenLine
                          size={14}
                          className="text-[#5652B0] mr-2"
                        />
                        <Typography
                          variant="body2"
                          className="text-gray-600 font-medium">
                          Note
                        </Typography>
                      </div>
                      <Typography
                        variant="body2"
                        className="text-gray-500 pl-6">
                        {receiptData.notes}
                      </Typography>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxWidth: "400px",
            width: "100%",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
          },
        }}>
        <DialogTitle
          sx={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: "#1F2937",
            borderBottom: "1px solid #E5E7EB",
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}>
          <Trash2 size={22} className="text-red-500" />
          Delete Receipt
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <DialogContentText className="text-gray-600 mb-2">
            Are you sure you want to delete receipt{" "}
            <span className="font-semibold">{receiptData.id}</span>?
          </DialogContentText>
          <DialogContentText className="text-red-500 text-sm">
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            padding: "16px 24px 24px",
            justifyContent: "flex-end",
            gap: "12px",
          }}>
          <Button
            onClick={() => setDeleteModalOpen(false)}
            sx={{
              color: "rgba(0, 0, 0, 0.6)",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "8px 16px",
            }}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            sx={{
              bgcolor: "#EF4444",
              "&:hover": { bgcolor: "#DC2626" },
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "8px 20px",
              boxShadow: "none",
            }}>
            {loading ? (
              <CircularProgress size={20} color="inherit" className="mr-2" />
            ) : (
              "Delete Receipt"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ViewReceiptDetail;
